import React, { useState, useRef } from 'react';
import {
	TextField,
	Button,
	Grid,
	Paper,
	MenuItem,
	Autocomplete,
	Box,
	Divider,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	IconButton,
	InputAdornment,
	Popper,
	ClickAwayListener,
	Checkbox,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	PostToDo,
	IToDoPost,
	getToDoPrioritys,
	getToDoStatus,
	getTodoById,
	UpdateToDo,
	getAllTodoCategories,
	getChecklistGroupsForTodo,
} from 'core/API/todo';
import { getUsersByWorkplace, GetFrequencyList } from 'core/API/checklist';
import { assignmentWorkplaceUserOptions } from 'core/utils/enrichWorkplaceUsers';
import { createToDoSchedule } from 'core/API/recurringTodo';
import RecurrencePicker from 'module/toDoPage/components/RecurrencePicker';
import {
	buildToDoSchedulePayload,
	DEFAULT_RECURRENCE_FORM,
	filterRecurringFrequencies,
	RecurrenceFormState,
	validateRecurrenceForm,
} from 'module/toDoPage/utils/recurringTodoUtils';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useWorkplaceUser } from 'module/documents/utils';
import styles from './AddToDo.module.scss';
import { useEffect } from 'react';

import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
	safeDateParse,
	formatDateForInput,
	formatDateForPayload,
	formatDateMMDDYYYY,
	getTodayISO,
	validateDateNotPast,
	getDateTimeForSorting,
	formatDatePickerForInput,
} from 'shared/utils/dateUtils';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
	getCategoryIri,
	getDefaultTodoCategoryIri,
} from 'module/toDoPage/utils/todoCategoryUtils';
import { useToDoPermissions } from 'module/toDoPage/hooks/useToDoPermissions';

// Add RouteParams type for useParams
type RouteParams = { id?: string };

const AddToDo: React.FC = () => {
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const history = useHistory();
	const workplace = useWorkplaceUser();
	const { id } = useParams<RouteParams>();
	const { canEdit: canEditToDo } = useToDoPermissions();

	// const userId = workplace?.data.user.id;
	const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');

	const [form, setForm] = useState({
		description: '',
		dueDate: getTodayISO(), // Set today as default
		dueTime: null as dayjs.Dayjs | null,
		noteMessage: '',
		priorityId: 1,
		statusId: 1,
		categoryIri: '',
		logId: 1,
		geolocation: {
			latitude: '',
			longitude: '',
		},
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [openBackDialog, setOpenBackDialog] = useState(false);
	const [assignmentType, setAssignmentType] = useState<'Person' | 'Group'>('Person');
	const [selectedValue, setSelectedValue] = useState<any>('');
	const [calendarOpen, setCalendarOpen] = useState(false);
	const calendarAnchorRef = useRef<HTMLDivElement>(null);
	const [isRecurring, setIsRecurring] = useState(false);
	const [recurrence, setRecurrence] = useState<RecurrenceFormState>(DEFAULT_RECURRENCE_FORM);
	const [recurrenceErrors, setRecurrenceErrors] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<{
		description?: string;
		dueDate?: string;
		assigned?: string;
		category?: string;
		noteMessage?: string;
	}>({});

	// Helper function to format date for display (MM/DD/YYYY)
	const formatDateForDisplay = (dateString: string): string => {
		return formatDateMMDDYYYY(dateString);
	};

	// Helper function to format time for display (24-hour format)
	const formatTimeForDisplay = (dateString: string): string => {
		if (!dateString) return '';
		const date = safeDateParse(dateString);
		if (!date) return '';

		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		return `${hours}:${minutes}`;
	};

	// Calendar change handler
	const handleCalendarChange = (val: Date | null) => {
		if (val) {
			const newISO = formatDatePickerForInput(val);
			setForm((prev) => ({ ...prev, dueDate: newISO }));
			setIsFormDirty(true);
		}
		setCalendarOpen(false);
	};

	useEffect(() => {
		if (id) {
			getTodoById(Number(id))
				.then((todo) => {
					if (todo.dueDate || todo.due_date) {
						const dateStr = formatDateForInput(todo.dueDate || todo.due_date);

						// Convert time string to Dayjs if it exists
						const timeObj = todo.dueTime ? dayjs(todo.dueTime) : null;

						setForm({
							description: todo.description || '',
							dueDate: dateStr,
							dueTime: timeObj,
							noteMessage: todo.note?.message || todo.note || '',
							priorityId: todo.priority?.id || 1,
							statusId: todo.status?.id || 1,
							categoryIri: getCategoryIri(todo.category),
							logId: todo.log?.id || 1,
							geolocation: {
								latitude: todo.geolocation?.latitude || '',
								longitude: todo.geolocation?.longitude || '',
							},
						});
					} else {
						setForm({
							description: todo.description || '',
							dueDate: getTodayISO(), // Set today as default if no due date
							dueTime: null,
							noteMessage: todo.note?.message || todo.note || '',
							priorityId: todo.priority?.id || 1,
							statusId: todo.status?.id || 1,
							categoryIri: getCategoryIri(todo.category),
							logId: todo.log?.id || 1,
							geolocation: {
								latitude: todo.geolocation?.latitude || '',
								longitude: todo.geolocation?.longitude || '',
							},
						});
					}

					// Handle assignment type based on API response structure
					if (todo.assignedGroup) {
						setAssignmentType('Group');
						setSelectedValue(todo.assignedGroup.id || todo.assignedGroup);
					} else if (todo.user) {
						setAssignmentType('Person');
						setSelectedValue(todo.user.id || todo.user);
					} else {
						setAssignmentType('Person');
						setSelectedValue('');
					}
				})
				.catch((error) => {
					console.error('Error fetching todo:', error);
					showToast('error', 'Failed to load todo details');
				});
		}
	}, [id]);

	const resetCreateForm = () => {
		setForm({
			description: '',
			dueDate: getTodayISO(),
			dueTime: null,
			noteMessage: '',
			priorityId: 1,
			statusId: 1,
			categoryIri: '',
			logId: 1,
			geolocation: {
				latitude: '',
				longitude: '',
			},
		});
		setSelectedValue('');
		setAssignmentType('Person');
		setIsRecurring(false);
		setRecurrence(DEFAULT_RECURRENCE_FORM);
		setRecurrenceErrors({});
	};

	const invalidateTodoQueries = () => {
		try {
			const workplaceIdLS = Number(localStorage.getItem('workplace-id') || '1');
			queryClient.invalidateQueries(['todos', workplaceIdLS]);
			queryClient.invalidateQueries(['todos-categorized', workplaceIdLS]);
			queryClient.invalidateQueries(['to-do-schedules', workplaceIdLS]);
		} catch (e) {
			console.warn('Failed to invalidate todos cache after add/update', e);
		}
	};

	const { mutate: postOrUpdateToDo, isLoading: isSavingAdHoc } = useMutation(
		(data: any) => (id ? UpdateToDo(Number(id), data) : PostToDo(data)),
		{
			onSuccess: () => {
				showToast('success', id ? 'ToDo updated successfully!' : 'ToDo created successfully!');
				if (!id) {
					resetCreateForm();
				}
				resetFormDirty();
				invalidateTodoQueries();
				history.push(ROUTES.TO_DO);
			},
			onError: (error) => {
				console.error('Todo operation failed:', error);
				showToast('error', id ? 'Failed to update ToDo' : 'Failed to create ToDo');
			},
		}
	);

	const { mutate: createRecurringTodo, isLoading: isSavingRecurring } = useMutation(
		(payload: ReturnType<typeof buildToDoSchedulePayload>) => createToDoSchedule(payload),
		{
			onSuccess: () => {
				showToast('success', 'Recurring to-do created successfully!');
				resetCreateForm();
				resetFormDirty();
				invalidateTodoQueries();
				history.push(ROUTES.TO_DO);
			},
			onError: (error) => {
				console.error('Recurring todo creation failed:', error);
				showToast('error', 'Failed to create recurring to-do');
			},
		}
	);

	const isLoading = isSavingAdHoc || isSavingRecurring;

	const { data: prioritysList, isLoading: isPrioritiesLoading } = useQuery(
		['todo-priorities', workplaceId],
		() => getToDoPrioritys(),
		{
			enabled: true,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const { data: statusList, isLoading: isStatusesLoading } = useQuery(
		['todo-statuses', workplaceId],
		() => getToDoStatus(),
		{
			enabled: true,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const { data: todoCategories, isLoading: isCategoriesLoading } = useQuery(
		['todo-categories', workplaceId],
		() => getAllTodoCategories(),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const { data: usersList, isLoading: isUsersLoading } = useQuery(
		['todo-users', workplaceId],
		() => getUsersByWorkplace(workplaceId, assignmentWorkplaceUserOptions),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	// Fetch groups from API
	const { data: frequencyList, isLoading: isFrequenciesLoading } = useQuery(
		['todo-frequencies', workplaceId],
		() => GetFrequencyList(),
		{
			enabled: !!workplaceId && isRecurring && !id,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	const { data: groupsList, isLoading: isGroupsLoading } = useQuery(
		['todo-groups', workplaceId],
		() => getChecklistGroupsForTodo(),
		{
			enabled: !!workplaceId,
			refetchOnMount: 'always',
			refetchOnWindowFocus: false,
			staleTime: 0,
		}
	);

	// Normalize API responses to support different shapes
	const normalizedPriorities = Array.isArray(prioritysList?.member)
		? prioritysList.member
		: Array.isArray(prioritysList?.['hydra:member'])
			? prioritysList['hydra:member']
			: Array.isArray(prioritysList?.items)
				? prioritysList.items
				: Array.isArray(prioritysList?.data)
					? prioritysList.data
					: Array.isArray(prioritysList)
						? prioritysList
						: [];

	const normalizedStatuses = Array.isArray(statusList?.member)
		? statusList.member
		: Array.isArray(statusList?.['hydra:member'])
			? statusList['hydra:member']
			: Array.isArray(statusList?.items)
				? statusList.items
				: Array.isArray(statusList?.data)
					? statusList.data
					: Array.isArray(statusList)
						? statusList
						: [];

	// Normalize groups data - prefer explicit arrays from API shapes
	const normalizedGroups = Array.isArray((groupsList as any)?.member)
		? (groupsList as any).member
		: Array.isArray((groupsList as any)?.['hydra:member'])
			? (groupsList as any)['hydra:member']
			: Array.isArray((groupsList as any)?.items)
				? (groupsList as any).items
				: Array.isArray((groupsList as any)?.data)
					? (groupsList as any).data
					: Array.isArray(groupsList as any)
						? (groupsList as any)
						: [];

	const noPriorities = !isPrioritiesLoading && normalizedPriorities.length === 0;
	const noStatuses = !isStatusesLoading && normalizedStatuses.length === 0;
	const noGroups = !isGroupsLoading && normalizedGroups.length === 0;
	const normalizedCategories = Array.isArray(todoCategories) ? todoCategories : [];
	const noCategories = !isCategoriesLoading && normalizedCategories.length === 0;
	const recurringFrequencyOptions = filterRecurringFrequencies(
		Array.isArray(frequencyList) ? frequencyList : []
	);
	const selectedCategory =
		normalizedCategories.find((cat: any) => getCategoryIri(cat) === form.categoryIri) ?? null;

	useEffect(() => {
		if (!isRecurring || recurringFrequencyOptions.length === 0) return;
		const match = recurringFrequencyOptions.find(
			(option) => option.label.toLowerCase() === recurrence.frequencyLabel.toLowerCase()
		);
		if (match && match.id !== recurrence.frequencyId) {
			setRecurrence((prev) => ({ ...prev, frequencyId: match.id }));
		}
	}, [isRecurring, recurringFrequencyOptions, recurrence.frequencyLabel, recurrence.frequencyId]);

	useEffect(() => {
		if (isCategoriesLoading || normalizedCategories.length === 0 || form.categoryIri) {
			return;
		}
		const defaultCategoryIri = getDefaultTodoCategoryIri(normalizedCategories);
		if (defaultCategoryIri) {
			setForm((prev) => ({ ...prev, categoryIri: defaultCategoryIri }));
		}
	}, [isCategoriesLoading, normalizedCategories, form.categoryIri]);

	// Workplace-scoped users from /workplaces/users
	const normalizedUsers = Array.isArray(usersList) ? usersList : [];

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setIsFormDirty(true);

		if (name === 'latitude' || name === 'longitude') {
			setForm((prev) => ({
				...prev,
				geolocation: {
					...prev.geolocation,
					[name]: value,
				},
			}));
		} else {
			setForm((prev) => ({ ...prev, [name]: value }));
		}

		// Clear per-field errors on change
		if (name === 'description') {
			setErrors((prev) => ({ ...prev, description: '' }));
		}
		if (name === 'dueDate') {
			setErrors((prev) => ({ ...prev, dueDate: '' }));
		}
		if (name === 'noteMessage') {
			setErrors((prev) => ({ ...prev, noteMessage: '' }));
		}
	};

	// Reset form dirty state when form is successfully submitted
	const resetFormDirty = () => {
		setIsFormDirty(false);
	};

	const handleAssignmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setAssignmentType(e.target.value as 'Person' | 'Group');
		setIsFormDirty(true);
		setSelectedValue('');
		setErrors((prev) => ({ ...prev, assigned: '' }));
	};

	const handleBackClick = () => {
		if (isFormDirty) {
			setOpenBackDialog(true);
		} else {
			history.push(ROUTES.TO_DO);
		}
	};

	const handleDialogClose = (save: boolean) => {
		setOpenBackDialog(false);
		if (save) {
			handleSubmit({ preventDefault: () => {} } as React.FormEvent);
		} else {
			history.push(ROUTES.TO_DO);
		}
	};

	const validateDueDate = (date: string, time: string) => {
		if (!date) return false;
		return validateDateNotPast(date, true);
	};

	// Enhanced validation for edit mode
	const validateForm = () => {
		const nextErrors: {
			description?: string;
			dueDate?: string;
			assigned?: string;
			category?: string;
			noteMessage?: string;
		} = {};
		if (!form.description.trim()) {
			nextErrors.description = 'Description is required';
		} else if (form.description.length > 250) {
			nextErrors.description = 'Description must be ≤ 250 characters';
		}
		if (!isRecurring) {
			if (!form.dueDate) {
				nextErrors.dueDate = 'Due date is required';
			} else if (!validateDueDate(form.dueDate, '')) {
				nextErrors.dueDate = 'Due date cannot be before today';
			}
		}
		if (!selectedValue) {
			nextErrors.assigned = `Please select a ${assignmentType === 'Person' ? 'person' : 'group'}`;
		}
		if (!form.categoryIri) {
			nextErrors.category = 'Category is required';
		}
		if (form.noteMessage && form.noteMessage.length > 500) {
			nextErrors.noteMessage = 'Notes must be ≤ 500 characters';
		}
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (!id && isRecurring) {
			const nextRecurrenceErrors = validateRecurrenceForm(recurrence);
			setRecurrenceErrors(nextRecurrenceErrors);
			if (Object.keys(nextRecurrenceErrors).length > 0) {
				return;
			}

			createRecurringTodo(
				buildToDoSchedulePayload({
					description: form.description,
					note: form.noteMessage || '',
					priorityId: form.priorityId,
					categoryIri: form.categoryIri,
					assignmentType,
					selectedValue: Number(selectedValue),
					recurrence,
				})
			);
			return;
		}

		const workplaceIdLS = Number(localStorage.getItem('workplace-id') || '1');
		const userIdLS = Number(localStorage.getItem('userId') || '1');

		// Use the imported formatDateForPayload function

		const body: any = {
			description: form.description,
			log: null,
			dueDate: formatDateForPayload(form.dueDate), // Format as MM/DD/YYYY for payload
			dueTime: form.dueTime ? form.dueTime.format('HH:mm:ss') : null,
			note: form.noteMessage || '',
			organization: '/api/organizations/1', // Static organization ID (left as-is)
			geolocation: [
				form.geolocation.latitude && form.geolocation.longitude
					? `${form.geolocation.latitude},${form.geolocation.longitude}`
					: '28.6139,77.2090',
			],
			workplace: `/api/workplaces/${workplaceIdLS}`,
			priority: `/api/priorities/${form.priorityId}`,
			status: `/api/statuses/${form.statusId}`,
			category: form.categoryIri,
			author: `/api/users/${userIdLS}`,
		};

		// Set user or group based on assignment type
		if (assignmentType === 'Person') {
			body.user = `/api/users/${selectedValue}`;
			body.assignedGroup = null; // Set assignedGroup to null when user is selected
		} else {
			body.assignedGroup = `/api/checklist_groups/${selectedValue}`; // Use assignedGroup field as requested
			body.user = null; // Set user to null when group is selected
		}

		try {
			postOrUpdateToDo(body);
		} catch (error) {
			console.error('Error submitting todo:', error);
			showToast('error', 'Failed to submit todo. Please try again.');
		}
	};

	if (!canEditToDo) {
		return (
			<Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
				<Typography color="error">You do not have permission to create or edit to-dos.</Typography>
				<Button onClick={() => history.push(ROUTES.TO_DO)} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}>
					Back to To-Dos
				</Button>
			</Box>
		);
	}

	return (
		<Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Button
					style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
					onClick={handleBackClick}
				>
					Back
				</Button>
				<Button
					type="submit"
					style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', padding: '5px 12px' }}
					disabled={isLoading}
					onClick={handleSubmit}
				>
					{isLoading ? 'Saving...' : id ? 'Update' : 'Save'}
				</Button>
			</Box>

			<Divider sx={{ mb: 2 }} />

			<Typography variant="h6" mb={2} className={styles.schedulerTitle}>
				{id ? 'Edit To-Do' : 'Create To-Do'}
			</Typography>

			<form onSubmit={handleSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Box className={styles.selectionBox}>
							{/* Removed subheading as requested */}
							<RadioGroup
								row
								value={assignmentType}
								onChange={handleAssignmentChange}
								className={styles.radioGroup}
								name="assignment-type"
							>
								<FormControlLabel
									value="Person"
									control={
										<Radio
											sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }}
											disabled={isLoading}
										/>
									}
									label="Person"
									disabled={isLoading}
								/>
								<FormControlLabel
									value="Group"
									control={
										<Radio
											sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }}
											disabled={isLoading}
										/>
									}
									label="Group"
									disabled={isLoading}
								/>
							</RadioGroup>
							<TextField
								select
								label={assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
								value={selectedValue || ''}
								onChange={(e) => {
									const value = Number((e.target as HTMLInputElement).value);
									setSelectedValue(value);
									setIsFormDirty(true);
								}}
								fullWidth
								required
								disabled={
									assignmentType === 'Person' ? isUsersLoading : isGroupsLoading || isLoading
								}
								InputLabelProps={{ shrink: true }}
								SelectProps={{
									MenuProps: {
										PaperProps: {
											style: { maxHeight: 320, overflowY: 'auto' },
										},
									},
									displayEmpty: true,
								}}
							>
								{!selectedValue && (
									<MenuItem value="" disabled>
										{assignmentType === 'Person' ? 'Select Person' : 'Select Group'}
									</MenuItem>
								)}
								{assignmentType === 'Person' ? (
									isUsersLoading ? (
										<MenuItem disabled value="">
											Loading users...
										</MenuItem>
									) : (
										(normalizedUsers || []).map((item: any) => (
											<MenuItem key={item.id} value={item.id}>
												{`${item.firstName || ''} ${item.lastName || ''}`.trim()}
											</MenuItem>
										))
									)
								) : isGroupsLoading ? (
									<MenuItem disabled value="">
										Loading groups...
									</MenuItem>
								) : noGroups ? (
									<MenuItem disabled value="">
										No groups found
									</MenuItem>
								) : (
									normalizedGroups.map((item: any) => (
										<MenuItem key={item.id} value={item.id}>
											{item.label || item.name}
										</MenuItem>
									))
								)}
							</TextField>
							{errors.assigned && (
								<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
									{errors.assigned}
								</Typography>
							)}
						</Box>
					</Grid>

					{!id && (
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={isRecurring}
										onChange={(e) => {
											setIsRecurring(e.target.checked);
											setIsFormDirty(true);
											setRecurrenceErrors({});
											if (!e.target.checked) {
												setRecurrence(DEFAULT_RECURRENCE_FORM);
											}
										}}
										disabled={isLoading}
										sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }}
									/>
								}
								label="Recurring To-Do"
							/>
						</Grid>
					)}

					{!id && isRecurring && (
						<Grid item xs={12}>
							<RecurrencePicker
								value={recurrence}
								onChange={(nextValue) => {
									setRecurrence(nextValue);
									setIsFormDirty(true);
									setRecurrenceErrors({});
								}}
								frequencyOptions={recurringFrequencyOptions}
								disabled={isLoading || isFrequenciesLoading}
								errors={recurrenceErrors}
							/>
						</Grid>
					)}

					<Grid item xs={12}>
						<TextField
							name="description"
							label="Description"
							fullWidth
							required
							value={form.description}
							onChange={handleChange}
							inputProps={{ maxLength: 250 }}
							disabled={isLoading}
						/>
						{errors.description && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
								{errors.description}
							</Typography>
						)}
					</Grid>

					{(!isRecurring || !!id) && (
					<Grid item xs={6}>
						<TextField
							name="dueDate"
							label="Due Date"
							fullWidth
							required
							value={form.dueDate ? formatDateForDisplay(form.dueDate) : ''}
							placeholder="MM/DD/YYYY"
							InputLabelProps={{ shrink: true }}
							disabled={isLoading}
							error={!!errors.dueDate}
							helperText={errors.dueDate}
							sx={{
								'& .MuiOutlinedInput-root': {
									borderRadius: '6px',
									fontSize: '0.95rem',
									backgroundColor: isLoading ? '#f5f5f5' : '#fff',
									color: isLoading ? '#999' : '#3c5471',
									fontFamily: 'Gotham',
									'& fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&:hover fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&.Mui-focused fieldset': {
										border: '1px solid rgba(0,0,0,0.23)',
									},
									'&.Mui-disabled': {
										backgroundColor: '#f5f5f5',
										color: '#999',
									},
								},
								'& input': {
									fontFamily: 'Gotham',
									color: isLoading ? '#999' : '#3c5471',
									'&.Mui-disabled': {
										color: '#999',
									},
								},
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											disabled={isLoading}
											onClick={() => !isLoading && setCalendarOpen(true)}
											size="small"
											sx={{
												color: isLoading ? '#999' : '#3c5471',
												'&:hover': {
													backgroundColor: 'rgba(0,0,0,0.04)',
												},
											}}
										>
											<CalendarTodayIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>

						{/* Calendar Popper */}
						<Popper
							open={calendarOpen && !isLoading}
							anchorEl={calendarAnchorRef.current}
							placement="top-start"
							style={{
								zIndex: 999999999999,
								position: 'fixed',
								top: '20px',
								left: '300px',
							}}
							modifiers={[
								{
									name: 'offset',
									options: {
										offset: [0, -8],
									},
								},
								{
									name: 'preventOverflow',
									options: {
										boundary: 'viewport',
										altBoundary: true,
									},
								},
								{
									name: 'flip',
									options: {
										fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
									},
								},
								{
									name: 'computeStyles',
									options: {
										gpuAcceleration: false,
									},
								},
							]}
						>
							<ClickAwayListener onClickAway={() => setCalendarOpen(false)}>
								<Box
									sx={{
										p: 0,
										mb: 1,
										borderRadius: '8px',
										overflow: 'hidden',
										backgroundColor: 'white',
										boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
										position: 'relative',
										'& .react-datepicker': {
											fontFamily: 'Gotham',
											border: 'none',
											borderRadius: '8px',
											fontSize: '14px',
											width: '290px',
											position: 'relative',
										},
										'& .react-datepicker__header': {
											backgroundColor: '#f8f9fa',
											borderBottom: '1px solid #e9ecef',
											borderRadius: '8px 8px 0 0',
											padding: '12px 0',
										},
										'& .react-datepicker__current-month': {
											color: '#3c5471',
											fontWeight: 'bold',
											fontSize: '16px',
											margin: '8px 0',
										},
										'& .react-datepicker__day-name': {
											color: '#6c757d',
											fontWeight: '600',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											margin: '2px',
											fontSize: '13px',
										},
										'& .react-datepicker__day': {
											color: '#3c5471',
											borderRadius: '4px',
											margin: '2px',
											width: '36px',
											height: '36px',
											lineHeight: '36px',
											fontSize: '14px',
											'&:hover': {
												backgroundColor: '#e3f2fd',
											},
											'&.react-datepicker__day--selected': {
												backgroundColor: '#2196f3',
												color: 'white',
												fontWeight: 'bold',
												'&:hover': {
													backgroundColor: '#1976d2',
												},
											},
											'&.react-datepicker__day--keyboard-selected': {
												backgroundColor: '#2196f3',
												color: 'white',
											},
											'&.react-datepicker__day--disabled': {
												color: '#ccc',
												cursor: 'not-allowed',
											},
											'&.react-datepicker__day--outside-month': {
												color: '#ccc',
											},
											'&.react-datepicker__day--today': {
												backgroundColor: '#e8f5e8',
												color: '#2e7d32',
												fontWeight: 'bold',
											},
										},
										'& .react-datepicker__navigation': {
											top: '12px',
											'& .react-datepicker__navigation-icon': {
												borderColor: '#3c5471',
												'&:hover': {
													borderColor: '#1976d2',
												},
											},
										},
										'& .react-datepicker__month-select, & .react-datepicker__year-select': {
											border: '1px solid #ddd',
											borderRadius: '4px',
											padding: '4px 8px',
											margin: '0 4px',
											fontSize: '14px',
											backgroundColor: 'white',
										},
										'& .react-datepicker__month-container': {
											backgroundColor: 'white',
										},
										'& .react-datepicker__month': {
											margin: '8px',
										},
										'& .react-datepicker__triangle': {
											display: 'none',
										},
									}}
								>
									<ReactDatePicker
										inline
										selected={form.dueDate ? safeDateParse(form.dueDate) : null}
										onChange={handleCalendarChange}
										showYearDropdown
										scrollableYearDropdown
										yearDropdownItemNumber={100}
										minDate={new Date()} // Prevent selecting past dates
										dateFormat="MM/dd/yyyy"
										showMonthDropdown
										dropdownMode="select"
										calendarStartDay={1} // Start week on Monday
										shouldCloseOnSelect={true}
									/>
								</Box>
							</ClickAwayListener>
						</Popper>
					</Grid>
					)}

					{(!isRecurring || !!id) && (
					<Grid item xs={6}>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								label="Due Time"
								value={form.dueTime}
								onChange={(newTime) => {
									setForm((prev) => ({ ...prev, dueTime: newTime }));
									setIsFormDirty(true);
								}}
								disabled={isLoading}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</LocalizationProvider>
					</Grid>
					)}

					<Grid item xs={6}>
						<TextField
							name="priorityId"
							select
							label="Priority"
							fullWidth
							value={form.priorityId}
							onChange={(e) => {
								const value = Number((e.target as HTMLInputElement).value);
								setForm((prev) => ({ ...prev, priorityId: value }));
								setIsFormDirty(true);
							}}
							disabled={isPrioritiesLoading || isLoading}
							SelectProps={{
								MenuProps: {
									PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
									disableScrollLock: true,
								},
							}}
						>
							{isPrioritiesLoading ? (
								<MenuItem disabled value="">
									Loading priorities...
								</MenuItem>
							) : noPriorities ? (
								<MenuItem disabled value="">
									No priorities found
								</MenuItem>
							) : (
								normalizedPriorities.map((priority: any) => (
									<MenuItem value={priority.id} key={priority.id}>
										{priority.label ?? priority.name}
									</MenuItem>
								))
							)}
						</TextField>
					</Grid>

					{(!isRecurring || !!id) && (
					<Grid item xs={6}>
						<TextField
							name="statusId"
							select
							label="Status"
							fullWidth
							value={form.statusId}
							onChange={(e) => {
								const value = Number((e.target as HTMLInputElement).value);
								setForm((prev) => ({ ...prev, statusId: value }));
								setIsFormDirty(true);
							}}
							disabled={isStatusesLoading || isLoading}
							SelectProps={{
								MenuProps: {
									PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } },
									disableScrollLock: true,
								},
							}}
						>
							{isStatusesLoading ? (
								<MenuItem disabled value="">
									Loading statuses...
								</MenuItem>
							) : noStatuses ? (
								<MenuItem disabled value="">
									No statuses found
								</MenuItem>
							) : (
								normalizedStatuses.map((status: any) => (
									<MenuItem value={status.id} key={status.id}>
										{status.label ?? status.name}
									</MenuItem>
								))
							)}
						</TextField>
					</Grid>
					)}

					<Grid item xs={12}>
						<Autocomplete
							options={normalizedCategories}
							getOptionLabel={(option: any) => option?.label || option?.name || ''}
							isOptionEqualToValue={(option: any, value: any) =>
								getCategoryIri(option) === getCategoryIri(value)
							}
							value={selectedCategory}
							onChange={(_, value: any) => {
								setForm((prev) => ({ ...prev, categoryIri: value ? getCategoryIri(value) : '' }));
								setIsFormDirty(true);
								setErrors((prev) => ({ ...prev, category: '' }));
							}}
							disableClearable
							openOnFocus
							selectOnFocus
							clearOnBlur={false}
							handleHomeEndKeys
							disabled={isCategoriesLoading || isLoading}
							noOptionsText={isCategoriesLoading ? 'Loading categories...' : 'No categories found'}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Category"
									required
									error={!!errors.category}
									helperText={
										errors.category || (noCategories ? 'No categories found' : '')
									}
								/>
							)}
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							name="noteMessage"
							label="Notes"
							multiline
							rows={3}
							fullWidth
							value={form.noteMessage}
							onChange={handleChange}
							disabled={isLoading}
							inputProps={{ maxLength: 500 }}
							error={!!errors.noteMessage}
							helperText={errors.noteMessage}
						/>
					</Grid>
				</Grid>
			</form>

			<Dialog open={openBackDialog} onClose={() => handleDialogClose(false)}>
				<DialogTitle>Unsaved Changes</DialogTitle>
				<DialogContent>
					You have unsaved changes. Would you like to save before leaving?
				</DialogContent>
				<DialogActions>
					<Button
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							padding: '5px 12px',
						}}
						onClick={() => handleDialogClose(false)}
					>
						Cancel
					</Button>
					<Button
						onClick={() => handleDialogClose(true)}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							padding: '5px 12px',
						}}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
};

export default AddToDo;
